import React, { createContext, useReducer, useContext } from "react";
import styled from "styled-components";
import { Modal } from "components/designV2/modal/modal";
import { Text } from "components/designV2/text/text";
import { Color, ColorV2 } from "config/colors";
import { alertActions } from "./alertStore.actions";
import {
  AlertActions,
  AlertState,
  alertStateReducer,
  initialState,
} from "./alertStore.reducer";
import { CustomIcon } from "components/icon/customIcon.component";
import { AutoCloseCountdown } from "components/designV2/autoCloseCountdown/autoCloseCountdown";

export type AlertDispatch = (action: AlertActions) => void;

const AlertStateContext = createContext<AlertState | undefined>(undefined);
const AlertDispatchContext = createContext<AlertDispatch | undefined>(
  undefined,
);

export const AlertStoreProvider: React.FC = ({ children }) => {
  const [state, dispatch] = useReducer(alertStateReducer, initialState);

  return (
    <AlertStateContext.Provider value={state}>
      <AlertDispatchContext.Provider value={dispatch}>
        {children}
        <Modal
          label=""
          size="compact"
          isOpen={state.show}
          onClose={() => {
            dispatch({ type: "HIDE_ALERT" });
          }}
          centerText
        >
          <ModalBody>
            <Text typography="h3" align="center" color={ColorV2.Blue1}>
              {state.title}
            </Text>
            {state.icon && (
              <CustomIcon
                name={state.icon}
                size="70px"
                color={Color.SeaBlue400}
              />
            )}
            {state.message && (
              <Text typography="b1" align="center" color={ColorV2.Blue1}>
                {state.message}
              </Text>
            )}
            {state.autoCloseMs != null && (
              <AutoCloseCountdown
                durationMs={state.autoCloseMs}
                onComplete={() => {
                  dispatch({ type: "HIDE_ALERT" });
                }}
              />
            )}
          </ModalBody>
        </Modal>
      </AlertDispatchContext.Provider>
    </AlertStateContext.Provider>
  );
};
export const useAlertStore = () => {
  const state = useContext(AlertStateContext);
  const dispatch = useContext(AlertDispatchContext);

  if (state === undefined || dispatch === undefined) {
    throw new Error("useAlertStore must be used within a AlertStoreProvider");
  }

  const actions = alertActions(dispatch, state);

  return [state, actions, dispatch] as const;
};

const ModalBody = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 14px;
  min-width: 270px;
  width: 100%;
`;
